import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, ArrowRight, Tag } from 'lucide-react';
import { blogPosts } from '../data/mock';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

export const Blog = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', ...new Set(blogPosts.map(post => post.category))];

  const filteredPosts = selectedCategory === 'All' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory);

  const handleReadMore = (slug) => {
    navigate(`/blog/${slug}`);
  };

  return (
    <section id="blog" className="py-24 bg-gradient-to-b from-white to-gray-50 min-h-screen">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <span className="inline-block px-4 py-2 bg-burgundy-100 text-burgundy-700 rounded-full text-sm font-semibold mb-4">
            Blog & Insights
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-navy-900 mb-6">
            Expert Insights on Auditing & Insurance
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            Stay informed with the latest updates, tips, and best practices in auditing, 
            insurance, and financial compliance.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-6 py-2 rounded-full font-medium transition-all ${
                selectedCategory === category
                  ? 'bg-burgundy-700 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border-2 border-gray-200'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Blog Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {filteredPosts.map((post) => (
            <article 
              key={post.id}
              className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 group border border-gray-100"
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={post.image} 
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute top-4 left-4">
                  <Badge className="bg-burgundy-700 text-white hover:bg-burgundy-800">
                    {post.category}
                  </Badge>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Meta Info */}
                <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    <span>{new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    <span>{post.readTime}</span>
                  </div>
                </div>

                {/* Title */}
                <h3 className="text-xl font-bold text-navy-900 mb-3 group-hover:text-burgundy-700 transition-colors line-clamp-2">
                  {post.title}
                </h3>

                {/* Excerpt */}
                <p className="text-gray-600 mb-4 line-clamp-3">
                  {post.excerpt}
                </p>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {post.tags.slice(0, 2).map((tag, index) => (
                    <span 
                      key={index}
                      className="inline-flex items-center gap-1 text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded-full"
                    >
                      <Tag className="w-3 h-3" />
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Read More Button */}
                <Button 
                  onClick={() => handleReadMore(post.slug)}
                  variant="outline"
                  className="w-full border-2 border-burgundy-700 text-burgundy-700 hover:bg-burgundy-700 hover:text-white group/btn"
                >
                  Read More
                  <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                </Button>
              </div>
            </article>
          ))}
        </div>

        {/* Call to Action */}
        <div className="max-w-4xl mx-auto mt-16 bg-gradient-to-r from-navy-900 to-navy-800 rounded-2xl p-10 text-center">
          <h3 className="text-3xl font-bold text-white mb-4">
            Need Professional Advice?
          </h3>
          <p className="text-white/90 text-lg mb-6">
            Our expertise goes beyond articles. Get personalized guidance for your specific needs.
          </p>
          <Button 
            onClick={() => navigate('/')}
            size="lg"
            className="bg-burgundy-700 hover:bg-burgundy-800 text-white px-8 py-6"
          >
            Schedule a Consultation
          </Button>
        </div>
      </div>
    </section>
  );
};
